import { ComponentProps, FC, useCallback, useEffect, useState } from "react"
import { ApiRequester } from "src/domain/api/ApiRequester"
import { profileAddApi } from "src/domain/api/profile/profileAddApi"
import { File } from "src/domain/entities/file"
import { Profile } from "../../../../domain/entities/profile"
import { useAuth, useSurnameListApi, useVillageListApi } from "../../../../domain/hooks"
import { FormikOnSubmit } from "../../../../utils/types"
import { IProfileAddFormFields } from "./IProfileAddFormFields"
import { ProfileAddDrawerFormView } from "./ProfileAddDrawerFormView"

interface Props
	extends Omit<
		ComponentProps<typeof ProfileAddDrawerFormView>,
		| "handleSubmit"
		| "villageList"
		| "setVillageSearchText"
		| "surnameList"
		| "setSelectedImage"
		| "setSurnameSearchText"
		| "handleUploadImage"
	> {
	onSuccess: (profile: Profile) => void
}

export const ProfileAddDrawerFormController: FC<Props> = ({ onSuccess, ...rest }) => {
	const { token } = useAuth()

	const [selectedImage, setSelectedImage] = useState<File>()

	const handleUploadImage = useCallback(
		async (file: any) => {
			const formData = new FormData()
			formData.append("file", file)

			const response = await new ApiRequester(
				"/file/upload/v1",
				"POST",
			).sendRequest(formData, token)

			setSelectedImage(response as File)
			return response as File
		},
		[token],
	)

	const handleSubmit = useCallback<FormikOnSubmit<IProfileAddFormFields>>(
		async (values, { setErrors }) => {
			try {
				values.profileImageId = selectedImage?.id
				const profile = await profileAddApi(values, token)
				onSuccess(profile)
				rest.onClose()
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Something went wrong"
				setErrors({
					firstName: {
						en: message,
					},
				})
			}
		},
		[onSuccess, rest, token, selectedImage],
	)

	const { fetchVillageList, villageList } = useVillageListApi()
	const [villageSearchText, setVillageSearchText] = useState("")
	useEffect(() => {
		fetchVillageList({
			search: villageSearchText,
			pagination: { limit: 20, page: 1 },
			fetch: {
				talukaDistrict: true,
			},
		})
	}, [fetchVillageList, villageSearchText])

	const { fetchSurnameList, surnameList } = useSurnameListApi()
	const [surnameSearchText, setSurnameSearchText] = useState("")
	useEffect(() => {
		fetchSurnameList({
			fetch: {
				subCaste: true,
			},
			search: surnameSearchText,
			pagination: { limit: 20, page: 1 },
		})
	}, [fetchSurnameList, surnameSearchText])

	return (
		<ProfileAddDrawerFormView
			handleSubmit={handleSubmit}
			villageList={villageList}
			setVillageSearchText={setVillageSearchText}
			surnameList={surnameList}
			setSelectedImage={setSelectedImage}
			setSurnameSearchText={setSurnameSearchText}
			handleUploadImage={handleUploadImage}
			selectedImage={selectedImage}
			{...rest}
		/>
	)
}
